import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IdCardIcon, Loader2Icon, PlusIcon } from '@/shared/ui/icons';
import { Button } from '@/shared/ui/buttons/Button';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useCargoIdGateStatus } from '@/entities/cargo-id';

type ParcelCreateGateProps = {
  /** Hide the "Bekor qilish" outline button (e.g. on the create page where the user reached this directly via URL). */
  hideCancel?: boolean;
  /** Override the cancel handler — defaults to navigate(-1). */
  onCancel?: () => void;
};

/**
 * Full-card gate shown when the user cannot create a parcel because they have no
 * Cargo ID. Rendered on the parcel-create page when the user lands there without one.
 */
export const FeaturesCargoIdParcelCreateGate = ({
  hideCancel = false,
  onCancel,
}: ParcelCreateGateProps) => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const gate = useCargoIdGateStatus();

  const handleCancel = (): void => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  if (gate.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2Icon className="h-8 w-8 animate-spin text-brand-600" aria-hidden />
      </div>
    );
  }

  if (gate.status === 'error') {
    return (
      <div className="p-4 lg:p-6">
        <ErrorState message={t('loadError')} onRetry={gate.refetch} />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-5 shadow-card">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
            <IdCardIcon className="h-6 w-6" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {t('parcelGate.label')}
            </p>
            <h2 className="truncate text-base font-semibold text-ink">
              {t('parcelGate.noneTitle')}
            </h2>
          </div>
        </div>
        <p className="text-sm text-ink-muted">{t('parcelGate.noneHelper')}</p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {!hideCancel && (
            <Button variant="outline" onClick={handleCancel}>
              {t('parcelGate.cancel')}
            </Button>
          )}
          <Button
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => navigate('/ids/request')}
          >
            {t('parcelGate.getCta')}
          </Button>
        </div>
      </div>
    </div>
  );
};
